// React, Next
import { useContext } from 'react'
import { useTranslation } from 'next-i18next'

// MUI
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

// Custom
import { GlobalContext } from 'context/GlobalContextProvider'

// Styles
import themeColors from '@/assets/theme/colors'
import breakpoints from '@/assets/theme/breakpoints'
import classes from './TermsOfUse-CSS'

export default function TermsOfUse() {
  const { t } = useTranslation('termsOfUse')
  const { screenDimensions } = useContext(GlobalContext)

  const wide = screenDimensions.width > breakpoints.mobileSmall
  const bodySize = wide ? '0.93rem' : '0.85rem'

  const h2 = (text: string) => (
    <Typography
      variant="h2"
      component="h2"
      sx={classes.h3}
      style={{ fontSize: wide ? '1.1rem' : '0.95rem' }}
    >
      {text}
    </Typography>
  )

  const p = (text: string) => (
    <Typography
      key={text}
      component="p"
      sx={classes.b2}
      style={{ fontSize: bodySize }}
    >
      {text}
    </Typography>
  )

  /** A numbered section: heading plus its clauses, in document order. */
  const section = (title: string, paragraphs: string[]) => (
    <Box component="section" key={title}>
      {h2(title)}
      {paragraphs.map(p)}
    </Box>
  )

  const prohibited = [
    t('sections.permittedUse.items.01'),
    t('sections.permittedUse.items.02'),
    t('sections.permittedUse.items.03'),
    t('sections.permittedUse.items.04')
  ]

  return (
    <Box sx={classes.mainDiv}>
      <Box
        sx={classes.sndDiv}
        style={{ minHeight: screenDimensions.height - 220 }}
      >
        <Typography
          variant="h1"
          sx={classes.h1}
          style={{ fontSize: wide ? '2rem' : '1.5rem' }}
        >
          {t('h1')}
        </Typography>

        <Typography component="p" sx={classes.b1}>
          {t('version')}
        </Typography>
        <Typography component="p" sx={classes.b1}>
          {t('update')}
        </Typography>

        {/* Which language version actually governs. */}
        <Typography
          component="p"
          sx={{
            ...classes.b1,
            marginTop: '1.25rem',
            marginBottom: '1.5rem',
            paddingLeft: '1rem',
            borderLeft: `3px solid ${themeColors.success.main}`,
            fontStyle: 'italic'
          }}
        >
          {t('bindingNote')}
        </Typography>

        {section(t('sections.provider.title'), [t('sections.provider.p1')])}

        {section(t('sections.scope.title'), [
          t('sections.scope.p1'),
          t('sections.scope.p2'),
          t('sections.scope.p3')
        ])}

        {section(t('sections.noAdvice.title'), [
          t('sections.noAdvice.p1'),
          t('sections.noAdvice.p2'),
          t('sections.noAdvice.p3'),
          t('sections.noAdvice.p4')
        ])}

        {section(t('sections.contactForm.title'), [
          t('sections.contactForm.p1'),
          t('sections.contactForm.p2'),
          t('sections.contactForm.p3')
        ])}

        <Box component="section">
          {h2(t('sections.permittedUse.title'))}
          {p(t('sections.permittedUse.p1'))}
          {p(t('sections.permittedUse.p2'))}

          <Box
            component="ul"
            sx={{
              margin: '0 0 0.5rem',
              paddingLeft: '1.5rem',
              color: themeColors.gray.mid
            }}
          >
            {prohibited.map(item => (
              <Typography
                key={item}
                component="li"
                sx={{ marginBottom: '0.5rem' }}
                style={{ fontSize: bodySize }}
              >
                {item}
              </Typography>
            ))}
          </Box>
        </Box>

        {section(t('sections.ip.title'), [
          t('sections.ip.p1'),
          t('sections.ip.p2')
        ])}

        {section(t('sections.warranties.title'), [
          t('sections.warranties.p1'),
          t('sections.warranties.p2'),
          t('sections.warranties.p3')
        ])}

        {section(t('sections.liability.title'), [
          t('sections.liability.p1'),
          t('sections.liability.p2'),
          t('sections.liability.p3')
        ])}

        {section(t('sections.dataProtection.title'), [
          t('sections.dataProtection.p1')
        ])}

        {section(t('sections.externalLinks.title'), [
          t('sections.externalLinks.p1')
        ])}

        {section(t('sections.finalProvisions.title'), [
          t('sections.finalProvisions.p1'),
          t('sections.finalProvisions.p2'),
          t('sections.finalProvisions.p3')
        ])}
      </Box>
    </Box>
  )
}
